import React from 'react';
import clsx from 'clsx';

import { useDragReorder } from './useDragReorder';

type DraggableListRenderState = {
  isDragOver: boolean;
  isDragging: boolean;
};

type DraggableListProps<T> = {
  items: T[];
  onChange: (items: T[]) => void;
  itemKey: (item: T, index: number) => React.Key;
  renderItem: (
    item: T,
    index: number,
    state: DraggableListRenderState
  ) => React.ReactNode;
  isItemDraggable?: (item: T, index: number) => boolean;
  wrapperClassName?: string;
  dragOverClassName?: string;
  containerClassName?: string;
};

const DraggableList = <T,>({
  items,
  onChange,
  itemKey,
  renderItem,
  isItemDraggable,
  wrapperClassName,
  dragOverClassName,
  containerClassName,
}: DraggableListProps<T>) => {
  const { getDragProps } = useDragReorder(items, onChange);

  return (
    <div className={containerClassName}>
      {items.map((item, index) => {
        const enabled = isItemDraggable?.(item, index) ?? true;
        const {
          draggable,
          onDragStart,
          onDragOver,
          onDragLeave,
          onDrop,
          onDragEnd,
          isDragOver,
          isDragging,
        } = getDragProps(index, enabled);

        return (
          <div
            key={itemKey(item, index)}
            draggable={draggable}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            className={clsx(
              wrapperClassName,
              enabled && 'cursor-grab',
              enabled && isDragging && 'cursor-grabbing',
              isDragOver && dragOverClassName
            )}
          >
            {renderItem(item, index, {
              isDragOver,
              isDragging,
            })}
          </div>
        );
      })}
    </div>
  );
};

export default DraggableList;
export { useDragReorder } from './useDragReorder';
